import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { format, addDays, startOfWeek, endOfWeek, subWeeks, addWeeks } from 'date-fns';
import { CommonModule } from '@angular/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { Timeslot, Appointment, Week } from '../../../interfaces/date-selection.interface';

@Component({
  selector: 'app-date-selection',
  standalone: true,
  imports: [TranslateModule, CommonModule, NzTagModule, NzDatePickerModule, NzCheckboxModule, ReactiveFormsModule, NzDividerModule, FormsModule, NzButtonModule, NzIconModule],
  templateUrl: './date-selection.component.html',
  styleUrl: './date-selection.component.scss',
})
export class DateSelectionComponent implements OnInit, OnChanges {
  /* TODO Sticky in mobile delle date selezionate */
  @Input() dates: any;
  @Input() oldDates: any;
  @Input() minAppointments: any;
  @Input() maxAppointments: any;
  @Input() startHour: any;
  @Input() endHour: any;
  @Input() selectedAppointments: any;
  @Output() dateSelectionEmitter: EventEmitter<Appointment[]> = new EventEmitter<Appointment[]>();
  manualDate: FormControl = new FormControl();
  currentStartDate!: Date;
  endDate!: Date;
  weekDays: Week[] = [];
  listSelectedAppointments: Array<Appointment> = [];

  constructor(
    protected translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.dates) {
      this.currentStartDate = new Date(this.dates[0].date);
      this.endDate = new Date(this.dates[this.dates.length - 1].date);
      if (this.selectedAppointments) {
        this.listSelectedAppointments = this.selectedAppointments;
        this.fillValuesCheckbox();
        this.checkCheckboxesStatus();
        this.emitDataSelection();
      }
      if (this.startHour && this.endHour) {
        this.dates = this.filterDates(this.dates, this.startHour, this.endHour);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dates']) {
      this.currentStartDate = new Date(this.dates[0].date);
      this.endDate = new Date(this.dates[this.dates.length - 1].date);
    }
  }

  protected onSelectedManualDateChange(manualDate: Date): void {
    this.currentStartDate = startOfWeek(manualDate, { weekStartsOn: 1 });
    // this.updateWeekDays();
    this.fillValuesCheckbox();
    this.checkCheckboxesStatus();
  }

  protected previousWeek(): void {
    const prevDate = subWeeks(this.currentStartDate, 1);
    const checkPrevDate = addDays(prevDate, 7);
    if (!this.checkDisabledDate(checkPrevDate)) {
      this.currentStartDate = prevDate;
    }
    this.fillValuesCheckbox();
    this.checkCheckboxesStatus();
    this.cdr.detectChanges();
  }

  protected nextWeek(): void {
    const oldCurrentStartDate = this.currentStartDate;
    this.currentStartDate = addWeeks(this.currentStartDate, 1);
    if (this.currentStartDate <= this.endDate) {
      this.fillValuesCheckbox();
      this.checkCheckboxesStatus();
    } else {
      this.currentStartDate = oldCurrentStartDate;
    }
    this.cdr.detectChanges();
  }

  private fillValuesCheckbox(): void {
    this.listSelectedAppointments.forEach((appointment: Appointment) => {
      const { timeFound } = this.getDayAndTime(appointment.date, appointment.time);
      if (timeFound) {
        timeFound.checked = true;
      }
    });
  }

  protected getWeekStart(): Date {
    const weekStartDate = new Date(this.currentStartDate);
    weekStartDate.setDate(weekStartDate.getDate());
    return weekStartDate;
  }

  protected getWeekEnd(): Date {
    return endOfWeek(this.currentStartDate, { weekStartsOn: 1 });
  }

  isDateInCurrentWeek(date: string): boolean {
    const targetDate = new Date(date);
    const start = this.getWeekStart();
    const end = this.getWeekEnd();
    return targetDate >= start && targetDate <= end;
  }

  private getDayAndTime(day: Date, value: string): { dayFound?: Week; timeFound?: Timeslot } {
    const dayFound = this.dates.find((week: Week) => new Date(week.date).getTime() === new Date(day).getTime());
    const timeFound = dayFound?.timeslots.find((timeslot: Timeslot) => timeslot.value === value);
    return { dayFound, timeFound };
  }

  protected selectedTimeslots(day: Date, event: Timeslot): void {
    const { dayFound, timeFound } = this.getDayAndTime(day, event.value);

    if (dayFound && timeFound) {
      if (timeFound.checked) {
        this.listSelectedAppointments.push({
          date: dayFound.date,
          time: timeFound.value,
          label: `${format(dayFound.date, 'EEEE dd')}, ${timeFound.label}`,
        });
      }
      if (!timeFound.checked) {
        this.deleteAppointment(dayFound.date, timeFound.value);
      }

      this.checkCheckboxesStatus();
      this.emitDataSelection();
    }
  }

  protected deleteAppointment(day: Date, time: string, fromTemplate?: boolean): void {
    this.listSelectedAppointments = this.listSelectedAppointments.filter((appointment: Appointment) => {
      const completeAppointment = appointment.date + appointment.time;
      return completeAppointment !== day + time;
    });

    if (fromTemplate) {
      this.disableSpecificCheckbox(day, time);
      this.emitDataSelection();
    }
  }

  protected disableCheckboxes(): void {
    this.dates.forEach((day: Week) => {
      day.timeslots.forEach((slot: Timeslot) => {
        if (!slot.checked) {
          slot.disabled = true;
        }
      });
    });
  }

  private checkCheckboxesStatus(): void {
    if (this.listSelectedAppointments.length === this.maxAppointments) {
      this.disableCheckboxes();
    } else {
      this.enableCheckboxes();
    }
  }

  protected enableCheckboxes(): void {
    this.dates.forEach((day: any, i: number) => {
      day.timeslots.forEach((slot: any, j: number) => {
        if (slot.disabled && !this.oldDates[i]?.timeslots[j]?.disabled) {
          slot.disabled = false;
        }
      });
    });
    this.cdr.detectChanges();
  }

  private disableSpecificCheckbox(day: Date, time: string): void {
    const { timeFound } = this.getDayAndTime(day, time);

    if (timeFound) {
      timeFound.checked = false;
    }
    this.checkCheckboxesStatus();
  }

  protected checkDisabledDate = (value: Date): boolean => {
    const today = new Date();
    value.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return value.getTime() <= today.getTime() || value.getTime() >= this.endDate.getTime();
  };

  private emitDataSelection(): void {
    if (this.listSelectedAppointments.length > 0) {
      this.dateSelectionEmitter.emit(this.listSelectedAppointments);
    }
  }

  private filterDates(dates: any, startRange: number, endRange: number): any {
    const now = new Date();
    return dates.filter((date: any) => {
      const timeDiff = (date.getTime() - now.getTime()) / (1000 * 60 * 60);
      return !(timeDiff >= 0 && timeDiff <= startRange) && !(timeDiff <= 0 && timeDiff >= -endRange);
    });
  }
}
